import { ATTRIBUTES, AVAILABILITY, BONUS_TYPES, BOX_TYPE, GET_STOCK, SEPARATOR } from '@/constants/product';
import { DEFAULT_RESOLUTION, RESOLUTION } from '@/constants/gui';
import { ERP_BOOLEAN } from '@/constants/keys';
import { FILTERS, IDENTIFIER, SORT_ENUM } from '@/constants/categ';
import { IMG_SIZE, URL_SELECTORS, URLS } from '@/constants/urls';
import { PRODUCT_QTY_MAX, SECTIONS_VALUE, WINEBOX_MAX } from '@/constants/cart';
import { STORE_ID } from '@/constants/stores';

export const isPrimeurCode = (productCode) => (
  productCode === SECTIONS_VALUE.PRIMEUR_NOT_USA || productCode === SECTIONS_VALUE.PRIMEUR_USA
);

export const getInitialItems = (product, isWinebox) => {
  const initialItems = isWinebox ? (product.itemsCP || []) : (product.items || []);
  return initialItems.filter((item) => item.stock > 0);
};

export const getPromotions = (groupIdentifier, items) => {
  let promoItems = items.filter((elem) => elem.attributes?.promotion_o_n?.valueId === ERP_BOOLEAN.TRUE);
  const promoNumber = groupIdentifier.split('_')[1];

  if (promoNumber) {
    promoItems = promoItems.filter((elem) => elem.attributes?.promotion?.valueId === promoNumber);
  }

  return promoItems;
};

export const filterListOnItemOnAttributeValue = (arrayFilter, attrName, items) => {
  let filterItemArray = [];
  // We have to filter arrayFilter by attrName to avoid the case => ['test:ok', 'toFind:ok', 'in_stock:1']
  // In this example, filterValues === ['ok', 'ok', '1']
  // With this filter, we have => ['ok']
  const arrayFilterByAttrName = arrayFilter.filter((v) => v.startsWith(attrName));
  const filterValues = arrayFilterByAttrName.filter((v) => v.indexOf('') > -1).map((e) => e.split(SEPARATOR.FILTER_VALUE)[1]) || [];

  filterValues.forEach((v) => {
    const array = items.filter((elem) => {
      if (attrName === FILTERS.IN_STOCK) {
        // TODO: Harmonize values for the US store
        // For now we have on product values 1, 2, 3 and on item values L, PA, P
        const inStockValueMapping = {
          1: AVAILABILITY.LETTERS.IN_STOCK,
          2: AVAILABILITY.LETTERS.PREARRIVAL,
          3: AVAILABILITY.LETTERS.PRIMEUR,
          L: AVAILABILITY.LETTERS.IN_STOCK,
          P: AVAILABILITY.LETTERS.PRIMEUR
        };

        const inStockValue = inStockValueMapping[v];
        const valueId = elem.attributes?.[attrName]?.valueId;

        return valueId !== undefined && valueId === inStockValue;
      }

      return elem.attributes?.[attrName]?.valueId === v;
    });

    if (array.length > 0 && filterItemArray.length === 0) {
      filterItemArray = array;
    } else if (array.length > 0) {
      filterItemArray = filterItemArray.concat(array);
    }
  });

  return filterItemArray;
};

export const filterListOfItems = (items, activefilter, arrayFilter) => {
  let filteredItems = [...items];
  const filters = [FILTERS.PACKAGING, FILTERS.DECLINAISON, FILTERS.PROMOTION, FILTERS.FORMAT, FILTERS.IN_STOCK];

  filters.forEach((filter) => {
    if (activefilter.includes(filter)) {
      filteredItems = filterListOnItemOnAttributeValue(arrayFilter, filter, filteredItems);
    }
  });

  // If the filters could not be cumulated, you have to apply them one by one until you get a result
  filters.reverse().forEach((filter) => {
    if (filteredItems.length === 0 && activefilter.includes(filter)) {
      filteredItems = filterListOnItemOnAttributeValue(arrayFilter, filter, items);
    }
  });

  return filteredItems || items;
};

export const filterListOfItemsOnCatDeclinaison = (catDeclinaisonSel, items, storeCode) => {
  let newItemsArray = items;
  if (catDeclinaisonSel[0].startsWith(IDENTIFIER.DECLINAISON)) {
    const [attr, attrValue] = catDeclinaisonSel[0].split(SEPARATOR.FILTER_VALUE);
    newItemsArray = items.filter((item) => {
      if ((item.attributes[attr.replace(SEPARATOR.STORE + storeCode, '')] || {}).valueId === attrValue) {
        return true;
      }
    });
    if (newItemsArray.length > 0) {
      return newItemsArray;
    }
  }
  for (const cat of catDeclinaisonSel) {
    newItemsArray = items.filter((item) =>
      item.partnumber && item.partnumber.indexOf(cat.toUpperCase()) > -1);
    if (newItemsArray.length > 0) {
      return newItemsArray;
    }
  }

  return newItemsArray.length > 0 ? newItemsArray : items;
};

export const getFilterValueByKey = (filters, filterKey) => filters.reduce((acc, v) => {
  const isPresent = v.match(new RegExp(`(${filterKey}:)(.+)`));
  return isPresent ? [...acc, isPresent[2]] : [...acc];
}, [])[0];

export const getMinFilterPriceItem = (product, items, arrayFilter) => {
  const minFilterPrice = parseInt(getFilterValueByKey(arrayFilter, FILTERS.MIN_PRICE), 10) || product.minPrice;

  const itemsShallowCopy = [...items].sort((a, b) => {
    return a.offerPrice - b.offerPrice;
  });

  const currentItem = itemsShallowCopy.find((item) => item.offerPrice >= minFilterPrice);

  return currentItem;
};

export const getMaxFilterPriceItem = (product, items) => {
  const maxFilterPrice = product.maxPrice;
  const currentItem = items.find((item) => item.offerPrice === maxFilterPrice);

  return currentItem;
};

export const getCurrentProductItem = (
  product, activefilter = '', sort, categ, searchMode, storeCode, isWinebox = false, isCU = false
) => {
  if (!product) {
    return {};
  }

  if (isCU) {
    return product.items[0];
  }

  if (categ?.isDynamic && categ?.dynamicCriteria) {
    activefilter = categ.dynamicCriteria + '|' + activefilter;
  }

  let items = getInitialItems(product, isWinebox);

  if (items.length === 1) {
    return items[0];
  }
  let currentItem = [];

  const arrayFilter = activefilter ? activefilter.split(SEPARATOR.FILTER_ELEMENT) : [];

  // first search if some activate need specific items
  items = filterListOfItems(items, activefilter, arrayFilter);
  if (items.length === 1) {
    return items[0];
  }

  // if sort by price is activate need to get the lowest or highest product of the list
  if ((sort === SORT_ENUM.PRICELESS || sort === SORT_ENUM.PRICEMOST) && items.length > 0) {
    const originalItems = product.items || [];
    const item = sort === SORT_ENUM.PRICELESS ? getMinFilterPriceItem(product, originalItems, arrayFilter) :
      getMaxFilterPriceItem(product, originalItems);
    currentItem.push(item);
  } else if (activefilter.indexOf(FILTERS.MIN_PRICE) > -1 || activefilter.indexOf(FILTERS.MAX_PRICE) > -1) {
    // if there is filter activate on min price and / or max price
    const minFilter = parseInt(getFilterValueByKey(arrayFilter, FILTERS.MIN_PRICE), 10) || product.minPrice;
    const maxFilter = parseInt(getFilterValueByKey(arrayFilter, FILTERS.MAX_PRICE), 10) || product.maxPrice;
    // search the products between min and max price
    currentItem = items.filter((el) =>
      el.offerPrice >= minFilter && el.offerPrice <= maxFilter
    );
  } else if (categ?.parentGroupIdentifier === IDENTIFIER.PROMOTION || categ?.groupIdentifier === IDENTIFIER.PROMOTION) {
    // if current categ is one in promotion search the product in promotion
    currentItem = getPromotions(categ.groupIdentifier, items);
  } else if (items.length > 1) {
    // if several item can been show choose the one matching with categ facette
    // check if there is a special config on the categ
    if (!searchMode && categ?.catDeclinaisonSel && categ?.catDeclinaisonSel.split(SEPARATOR.CATEG_DECL).length > 0) {
      items = compareByConditionnement(
        filterListOfItemsOnCatDeclinaison(categ.catDeclinaisonSel.split(SEPARATOR.CATEG_DECL), items, storeCode)
      );
    } else { // if nothing found before get default format
      if (storeCode === STORE_ID.USA) {
        return getUSAFacette(items);
      }

      if (storeCode === STORE_ID.SINGAPORE && (activefilter === '' || activefilter.includes('in_stock:L'))) {
        const itemSG = items.find((item) => item.stockOrigin === 'SG');
        if (itemSG !== undefined) {
          return itemSG;
        }
      }

      const itemsCFormat = findBottleByConditionnement(items);
      if (itemsCFormat.length > 0) {
        return compareByConditionnement(itemsCFormat)[0];
      }
    }
  }
  if (activefilter.includes(FILTERS.ONLY_CU) && product.hasUnitFormat) {
    const itemList = items?.length > 0 ? items : product.items || [];
    const itemCU = itemList.find((item) =>
      item.attributes?.conditionnement?.valueId === 'CU' ||
      item.partnumber?.includes('C/NC/1')
    );

    if (itemCU) return itemCU;
  }

  if ((!currentItem || currentItem.length === 0) && items.length > 0) {
    return items.find((item) => item.stock > 0) || items[0];
  } else if (!currentItem || currentItem.length === 0 || !currentItem[0]) {
    return getFirstWithStockOrFirst((product.items || []));
  }
  return currentItem[0];
};

export const getUSAFacette = (items) => {
  const inStockItems = items.filter((item) => item.attributes.in_stock.valueId === SECTIONS_VALUE.IN_STOCK_NOT_USA);
  if (inStockItems && inStockItems.length > 0) {
    // return smallest format in stock
    return compareByConditionnement(inStockItems)[0];
  } else {
    // if nothing is in stock, just return smallest format
    return compareByConditionnement(items)[0];
  }
};

export const findBottleByConditionnement = (bottles) => bottles.filter((bottle) => {
  const cond = getAttributeId(bottle, ATTRIBUTES.CONDITIONING);
  return (cond === BOX_TYPE.STANDARD || cond === BOX_TYPE.BOTTLE_CASE || cond === BOX_TYPE.CARDBOARD_BOX);
});

// TODO TYPESCRIPT : bug corrected => use slice for bottles array immutability
// (remove comment whe validated)
export const compareByConditionnement = (bottles) => bottles.slice().sort((a, b) => {
  const boxTypeArr = Object.values(BOX_TYPE);
  const condA = boxTypeArr.indexOf(getAttributeId(a, ATTRIBUTES.CONDITIONING));
  const condB = boxTypeArr.indexOf(getAttributeId(b, ATTRIBUTES.CONDITIONING));
  if (condA !== -1 && condB !== -1) {
    return condA - condB;
  } else if (condB !== -1 && condA === -1) {
    return 1;
  } else if (condB === -1 && condA !== -1) {
    return -1;
  } else {
    return 0;
  }
});

export const isBoxProduct = (partnumber) => /\/W[A-Z][LP]\//g.test(partnumber);

export const getFirstWithStockOrFirst = (items) => items.find((item) => item.stock > 0) || items[0];

/**
 * Return the max item quantity that can be bought by one user
 * @param {object} productsService The products service
 * @param {*} item The product item
 * @param {*} limitEquivbtl
 * @param {boolean} isActAsCustomer
 * @param {*} quantity
 * @param {string} storeId
 * @return {number}
 */
export const getProductMaxQuantity = async (productsService, item, limitEquivbtl, isActAsCustomer = false, quantity, storeId) => {
  let qteMax;

  if (isActAsCustomer && storeId === STORE_ID.USA) {
    qteMax = PRODUCT_QTY_MAX;
  } else if (!isActAsCustomer &&
    storeId === STORE_ID.USA &&
    item?.attributes?.qte_vte_max?.value &&
    item?.attributes?.qte_vte_max?.value < quantity) {
    qteMax = quantity;
  } else {
    qteMax = +getAttributeValue(item, 'qte_vte_max', PRODUCT_QTY_MAX);
  }

  const isWinebox = isBoxProduct(item.partnumber);

  if (limitEquivbtl > 0) {
    const nbunit = +getAttributeValue(item, 'nbunit', 1);
    const equivbtl = +getAttributeValue(item, 'equivbtl', 1);
    const qtyEquiv = Math.floor(limitEquivbtl / (nbunit * equivbtl));
    if (qteMax > qtyEquiv) {
      qteMax = qtyEquiv;
    }
  }

  if (isWinebox && qteMax > WINEBOX_MAX) {
    qteMax = WINEBOX_MAX;
  }

  const itemStock = +(item?.stock ?? 0);

  if (!itemStock) {
    return await productsService.getStock(item.partnumber, item.storeId, GET_STOCK.SHORT).then(
      ({ remainingQuantity }) => qteMax > remainingQuantity ? remainingQuantity : qteMax
    );
  }

  return qteMax > itemStock ? itemStock : qteMax;
};

export const isLightPrice = (product) => product && product.item &&
  product.item.offerPrice !== product.item.listPrice && product.item.offerPrice > 0;

export const isItemLightPriceUSA = (item, storeId) => item &&
  item.offerPrice !== item.listPrice && item.offerPrice > 0 && storeId === STORE_ID.USA;

export const getPackagingFromItem = (item, isBox = false) => [
  getAttributeValue(item, 'packaging'),
  getAttributeValue(item, 'nbunit'),
  getAttributeValue(item, 'conditionnement')
].filter((item, index) => item && item !== '-' && (!isBox || index !== 0)).join(' ');

export const getCurrentItemPrices = (currentItem) => {
  const nbunit = currentItem.attributes ? currentItem.attributes.nbunit?.value : 1;
  const equivbtl = currentItem.attributes ? currentItem.attributes.equivbtl?.value : 1;
  const formattedItem = { ...currentItem };

  formattedItem.unitPrice = parseFloat(formattedItem.offerPrice) / parseInt(nbunit, 10);
  formattedItem.priceLiter = ((parseFloat(formattedItem.offerPrice) / parseInt(nbunit)) / parseFloat(equivbtl)) / 0.75;

  return formattedItem;
};

export const getUnitPriceCurrentItem = (itemJson) => {
  const nbunit = itemJson.attributes ? itemJson.attributes.nbunit.value : 1;
  return parseFloat(itemJson.offerPrice) / parseInt(nbunit, 10);
};

export const isProductTtc = (product, currentItem) => {
  return !isPrimeurCode(getAttributeId(product, 'in_stock')) ? true :
    isProductTtcWithTaxRate(currentItem?.taxRate);
};

export const isProductTtcWithTaxRate = (taxRate) => taxRate !== 'Z';

export const isPrimeurFreeDelivery = (id) => id && id.match(/^(?!(.{3}CH)).*Primeur$/);

export const isPrimeurId = (id) => id && id.match(/.*Primeur$/);

export const isPreArrival = (id) => id && id.match(/.*PreArrival$/);

export const isPrimeurInCart = (cart) => !!cart?.products?.some((item) => item.product.primeur);

// Item attributes helpers
export const getAttribute = ({ attributes }, name) =>
  (attributes && attributes[name]) ?? {};

export const getAttributeUrl = ({ attributes }, name, defaultValue) =>
  (attributes && attributes[name]?.url) ?? defaultValue;

export const getAttributeValue = ({ attributes }, name, defaultValue) =>
  (attributes && attributes[name]?.value) ?? defaultValue;

export const getAttributeId = ({ attributes }, name, defaultValue) =>
  (attributes && attributes[name]?.valueId) ?? defaultValue;

export const getAttributeShortValue = ({ attributes }, name, defaultValue) =>
  (attributes && attributes[name]?.shortValue) ?? defaultValue;

export const getAttributeGroupIdentifier = ({ attributes }, name, defaultValue) =>
  (attributes && attributes[name]?.groupIdentifier) ?? defaultValue;

export const getAttributeIsSpirit = ({ attributes }, name, defaultValue) =>
  (attributes && attributes[name]?.isSpirit) ?? defaultValue;

// Return winenox text key
export const getWineboxName = (isPrimeur) =>
  isPrimeur ? 'winebox.WCP' : 'winebox.WCL';

export const getWineboxContent = (products) =>
  products.map((p) =>
    `${p.quantity} x ${p.product.productName} - ${getAttributeValue(p.product, 'couleur')}`
  ).join('<br />');

// Product images helper
export const getProductImage = (url, isWinebox, size = DEFAULT_RESOLUTION) => {
  const imgName = url && url.includes('/') ? url.slice(url.lastIndexOf('/') + 1) : url;
  const sz = RESOLUTION[size] || RESOLUTION[DEFAULT_RESOLUTION];
  if (imgName) {
    return `${URLS.IMG.replace('{0}', sz)}/${imgName}`;
  } else {
    return isWinebox ? URLS.DEFAULT_WINEBOX_IMAGE : `${URLS.NOIMG.replace('{0}', sz)}`;
  }
};

export const countOthersAvailablesFormats = (currentItemFormatValueId, productItems) => {
  let count = 0;
  productItems?.forEach((item) => {
    const valueId = getAttributeId(item, 'format');
    if (currentItemFormatValueId !== valueId) {
      count++;
    }
  });

  return count;
};

export const getCurrentItem = (product, item) => {
  const items = (product.items || []);
  let currentItem = {};

  // Item is set the current url is an item url
  if (item) {
    currentItem = items.filter((e) => item.startsWith(e.partnumber));
  } else if (product.items?.length > 0) {
    currentItem = findBottleByConditionnement(items);
  }

  if (currentItem.length > 0) {
    currentItem = currentItem[0];
  } else if (product.items?.length > 0) {
    currentItem = product.items[0];
  }
  return currentItem;
};

export const buildImage = (image, size = IMG_SIZE.THUMB) => URLS.BONUS_IMAGE
  .replace(URL_SELECTORS.SIZE, size)
  .replace(URL_SELECTORS.IMAGE_NAME, image.attachementName)
  .replace(URL_SELECTORS.IMAGE_EXTENSION, image.extension || 'jpg');

export const buildVideo = ({ url }) => ({
  idYoutube: url,
  img: URLS.BONUS_VIDEO.replace(URL_SELECTORS.IMAGE_NAME, url)
});

export const buildCarouselContent = (
  { bonus, image, video, productName },
  mainImageSize = DEFAULT_RESOLUTION,
  includedVideo = true,
  itemImg
) => {
  const images = bonus?.map((url) => ({
    value: url,
    thumbUrl: buildImage(url, IMG_SIZE.THUMB),
    urlFull: buildImage(url, IMG_SIZE.FULL),
    type: BONUS_TYPES.BONUS,
    name: productName
  })) ?? [];

  images.unshift({
    value: itemImg ? itemImg : image || 'no-image-icon',
    thumbUrl: getProductImageThumbUrl(itemImg ? itemImg : image),
    urlFull: getProductImage(itemImg ? itemImg : image, false, mainImageSize),
    type: BONUS_TYPES.IMAGE,
    name: productName
  });

  if (video?.length && includedVideo) {
    images.push(
      ...video.map((video) => {
        const value = buildVideo(video);

        return ({
          value,
          thumbUrl: `${URLS.IMG_THUMB}/img-video.jpg`,
          urlFull: `${URLS.IMG.replace('{0}', '531')}/img-video.jpg`,
          type: BONUS_TYPES.VIDEO,
          name: productName
        });
      })
    );
  }

  return images;
};

const getProductImageThumbUrl = (image) => image ? `${URLS.IMG_THUMB}/${image}` : `${URLS.NOIMG.replace('{0}', 150)}`;

export const findObjectByKey = (arr, key, predicate) => {
  return arr.find(
    (element) => element?.[key] === predicate
  );
};

export const findAttributeValueOfObjectByKey = (arr, key, predicate, attribute) => {
  return arr.find(
    (element) => element?.[key] === predicate
  )?.[attribute];
};

export const deleteAttributeFromObject = (obj, key) => {
  const { [key]: deleted, ...rest } = obj;
  return {
    deleted,
    rest
  };
};

export const addAttributeToObject = (obj, key, value) => {
  return {
    ...obj,
    [key]: value
  };
};

export const transformAttributes = (product, item) => {
  const productAttributes = product.attributes ? Object.values(product.attributes) : [];
  const itemAttributes = item.attributes ? Object.values(item.attributes) : [];

  return productAttributes.concat(itemAttributes)
    .sort((p1, p2) => (p1.sequence > p2.sequence) ? 1 :
      (p1.sequence < p2.sequence) ? -1 : 0)
    .filter((v, i, a) =>
      a.findIndex((v2) =>
        (v2.name?.toLowerCase() === v.name?.toLowerCase())) === i && v.value
    );
};

export const convertToWebPUrl = (imgUrl) => {
  if (!imgUrl) return imgUrl;
  const urlWithWebpExt = imgUrl.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  if (imgUrl.includes('/attachements/h')) {
    return urlWithWebpExt.replace('/attachements/h', '/attachements/webp/h');
  }

  if (imgUrl.includes('/attachements/listing/')) {
    return urlWithWebpExt.replace('/attachements/listing/', '/attachements/webp/listing/');
  }

  if (imgUrl.includes('/attachements/bonus/full/')) {
    return urlWithWebpExt.replace('/attachements/bonus/full/', '/attachements/webp/bonus/full/');
  }

  if (imgUrl.includes('/attachements/bonus/thumb/')) {
    return urlWithWebpExt.replace('/attachements/bonus/thumb/', '/attachements/webp/bonus/thumb/');
  }

  if (imgUrl.includes('/attachements/neutre/')) {
    return urlWithWebpExt.replace('/attachements/neutre/', '/attachements/webp/neutre/');
  }

  return urlWithWebpExt;
};
